import { IconProps, Loading } from '@drift-labs/icons';
import Link from 'next/link';
import { ComponentProps, ComponentType, memo, Ref } from 'react';
import {
	STANDARD_BUTTON_HORIZONTAL_PADDING_CLASSNAME,
	STANDARD_BUTTON_TEXT_SIZE_CLASSNAME,
	STANDARD_BUTTON_VERTICAL_PADDING_CLASSNAME,
	STANDARD_BUTTON_DISABLED_CLASSNAME,
} from 'src/components/primitives/Button/consts';
import { ButtonSize } from 'src/components/primitives/Button/types';
import { cj, cm } from 'src/utils/classNames';
import { WithChildren, WithClassName, WithRef } from 'src/utils/UtilityTypes';

interface BaseProps
	extends
		WithChildren,
		WithClassName,
		Pick<
			React.ButtonHTMLAttributes<HTMLButtonElement>,
			'disabled' | 'style' | 'id' | 'type'
		> {
	size: ButtonSize;
	variant?: 'primary' | 'secondary' | 'stroke' | 'positive' | 'negative';
	/**
	 * If `true`, renders a spinner on the left-hand side and sets `disabled` to `true`.
	 * If `leftIcon` is passed, the spinner replaces it.
	 */
	isLoading?: boolean;
	dataTestId?: string;
}

interface WithLeftIconProps {
	leftIcon?: ComponentType<IconProps>;
	rightIcon?: never;
}

interface WithRightIconProps {
	leftIcon?: never;
	rightIcon?: ComponentType<IconProps>;
}

type AsButtonProps = {
	as?: 'button' | undefined;
	href?: never;
	openInNewTab?: never;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

type AsLinkProps = {
	as: 'a';
	href: ComponentProps<typeof Link>['href'];
	openInNewTab?: boolean;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

type ButtonProps = WithRef<
	(BaseProps & (AsButtonProps | AsLinkProps)) &
		(WithLeftIconProps | WithRightIconProps),
	HTMLButtonElement | HTMLAnchorElement | HTMLDivElement
>;

function Button({
	size,
	variant = 'primary',
	className: classNameFromProps,
	leftIcon: LeftIcon,
	rightIcon: RightIcon,
	isLoading,
	disabled,
	children,
	dataTestId,
	as,
	href,
	openInNewTab,
	ref,
	onClick,
	...buttonPassThroughProps
}: ButtonProps) {
	const variantClassName = {
		primary: cj(
			'bg-button-primary-bg text-white hover:bg-button-primary-bg-hover'
		),
		secondary: cj(
			'bg-button-secondary-bg text-text-default hover:bg-button-secondary-bg-hover'
		),
		stroke: cj(
			'bg-input-bg text-text-default hover:bg-button-secondary-bg-hover',
			// We use `outline` instead of `border` to maintain consistent height with non-stroke variants.
			'outline -outline-offset-1 outline-button-secondary-bg outline-solid'
		),
		positive: cj('bg-positive-green text-white hover:bg-green-600'),
		negative: cj('bg-negative-red text-white hover:bg-red-600'),
	}[variant];

	const roundedClassName = {
		xl: 'rounded-sm',
		lg: 'rounded-sm',
		md: 'rounded-sm',
		sm: 'rounded-xs',
	}[size];

	const iconSize = {
		xl: 20,
		lg: 16,
		md: 14,
		sm: 12,
	}[size];

	const className = cm(
		'flex items-center justify-center gap-x-xs whitespace-nowrap',
		STANDARD_BUTTON_DISABLED_CLASSNAME,
		STANDARD_BUTTON_HORIZONTAL_PADDING_CLASSNAME[size],
		STANDARD_BUTTON_VERTICAL_PADDING_CLASSNAME[size],
		STANDARD_BUTTON_TEXT_SIZE_CLASSNAME[size],
		variantClassName,
		roundedClassName,
		classNameFromProps
	);

	const content = (
		<>
			{/* `shrink-0` on icons to ensure they don't shrink in WebKit. */}
			{LeftIcon && !isLoading && (
				<LeftIcon className="shrink-0" size={iconSize} />
			)}
			{isLoading && <Loading className="animate-spin" size={iconSize} />}
			{children}
			{RightIcon && <RightIcon className="shrink-0" size={iconSize} />}
		</>
	);

	const commonProps = {
		className,
		children: content,
		'data-testid': dataTestId,
	};

	if (as === 'a') {
		// We can't easily disable link elements, so we just return a div with the same styling.
		if (disabled) {
			return (
				<div
					ref={ref as Ref<HTMLDivElement>}
					data-disabled={disabled}
					{...commonProps}
				/>
			);
		}

		return (
			<Link
				href={href}
				target={openInNewTab ? '_blank' : undefined}
				rel={openInNewTab ? 'noopener noreferrer' : undefined}
				ref={ref as Ref<HTMLAnchorElement>}
				onClick={onClick}
				{...commonProps}
			/>
		);
	}

	return (
		<button
			disabled={isLoading || disabled}
			ref={ref as Ref<HTMLButtonElement>}
			onClick={onClick}
			{...buttonPassThroughProps}
			{...commonProps}
		/>
	);
}

export default memo(Button);
